import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, CalendarDays, DollarSign, Clock, Star, Phone, Mail, User, Send, FileText, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientProfilePage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [noteText, setNoteText] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/clients/${clientId}/profile`, { withCredentials: true });
      setData(d);
    } catch { toast.error('Client not found'); navigate('/clients'); }
  }, [clientId, navigate]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/clients/${clientId}/note`, { content: noteText }, { withCredentials: true });
      setNoteText('');
      fetchProfile();
      toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  if (!data) return <div className="p-8 text-center text-[#5C6656]">Loading...</div>;

  const { client, stats, top_services, appointments, invoices, communications } = data;
  const statusColor = { confirmed: 'bg-[#556B2F] text-white', completed: 'bg-[#3E6641] text-white', cancelled: 'bg-[#E2725B] text-white', 'no-show': 'bg-[#C0392B] text-white' };

  return (
    <div className="fade-in space-y-6" data-testid="client-profile-page">
      <button onClick={() => navigate('/clients')} className="flex items-center gap-1 text-sm text-[#556B2F] hover:underline"><ArrowLeft size={14} /> Back to Clients</button>

      {/* Header */}
      <div className="flex items-start gap-5">
        <div className="w-20 h-20 rounded-full bg-[#E9EDDF] flex items-center justify-center text-3xl font-bold text-[#556B2F]">{client.name?.charAt(0)?.toUpperCase()}</div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{client.name}</h1>
          <div className="flex items-center gap-4 mt-1 text-sm text-[#5C6656]">
            {client.email && <span className="flex items-center gap-1"><Mail size={13} /> {client.email}</span>}
            {client.phone && <span className="flex items-center gap-1"><Phone size={13} /> {client.phone}</span>}
            {client.gender && <span className="flex items-center gap-1"><User size={13} /> {client.gender}</span>}
            {client.birthday && <span className="flex items-center gap-1"><CalendarDays size={13} /> {client.birthday}</span>}
          </div>
          {client.tags?.length > 0 && <div className="flex gap-1 mt-2">{client.tags.map(t => <Badge key={t} className="bg-[#E9EDDF] text-[#556B2F] text-xs">{t}</Badge>)}</div>}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total Visits', value: stats.total_visits, icon: CalendarDays },
          { label: 'Total Spend', value: `\u00A3${stats.total_spend.toFixed(2)}`, icon: DollarSign },
          { label: 'Last Visit', value: stats.last_visit || 'Never', icon: Clock },
          { label: 'Cancelled', value: stats.cancelled, icon: AlertTriangle },
          { label: 'No-Shows', value: stats.no_shows, icon: AlertTriangle },
        ].map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i} className="border-[#E5E7E1]"><CardContent className="p-4">
              <div className="flex items-center gap-2"><Icon size={16} className="text-[#556B2F]" /><p className="text-xs font-bold uppercase text-[#5C6656]">{s.label}</p></div>
              <p className="text-xl font-bold text-[#1A2421] mt-1">{s.value}</p>
            </CardContent></Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Appointments + Invoices */}
        <div className="lg:col-span-2 space-y-6">
          {/* Top Services */}
          {top_services.length > 0 && (
            <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Favourite Services</CardTitle></CardHeader>
              <CardContent><div className="flex flex-wrap gap-2">{top_services.map(s => (
                <div key={s.name} className="px-3 py-1.5 bg-[#F5F7F0] rounded-lg text-sm"><span className="font-medium text-[#1A2421]">{s.name}</span> <span className="text-[#5C6656]">x{s.count}</span></div>
              ))}</div></CardContent>
            </Card>
          )}

          {/* Visit History Timeline */}
          <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Visit History ({stats.total_appointments})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                {appointments.length === 0 && <p className="px-5 py-8 text-center text-[#5C6656]">No visits yet</p>}
                {(() => {
                  let lastMonth = '';
                  return appointments.map((a, idx) => {
                    const month = a.date?.substring(0, 7);
                    const showMonth = month !== lastMonth;
                    lastMonth = month;
                    const isCompleted = a.status === 'completed';
                    const isCancelled = a.status === 'cancelled';
                    const isNoShow = a.status === 'no-show';
                    return (
                      <div key={a.id}>
                        {showMonth && (
                          <div className="px-5 py-2 bg-[#F5F7F0] border-b border-[#E5E7E1] sticky top-0 z-10">
                            <p className="text-xs font-bold text-[#556B2F] uppercase tracking-wider">{(() => { try { const [y,m] = month.split('-'); return new Date(y, m-1).toLocaleDateString('en-GB', {month:'long',year:'numeric'}); } catch { return month; } })()}</p>
                          </div>
                        )}
                        <div className="flex gap-3 px-5 py-3 border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6] transition-colors">
                          <div className="flex flex-col items-center flex-shrink-0 mt-0.5">
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isCompleted ? 'bg-[#3E6641]' : isCancelled ? 'bg-[#E2725B]' : isNoShow ? 'bg-[#C0392B]' : 'bg-[#556B2F]'}`} />
                            {idx < appointments.length - 1 && <div className="w-px flex-1 bg-[#E5E7E1] mt-1" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-[#1A2421]">{a.service_name}</p>
                              <Badge className={`text-[8px] capitalize ${statusColor[a.status] || 'bg-[#E5E7E1] text-[#5C6656]'}`}>{a.status}</Badge>
                            </div>
                            <p className="text-xs text-[#5C6656] mt-0.5">{a.date} at {a.start_time} · {a.duration}min with {a.staff_name}</p>
                            {(a.final_total || a.price) > 0 && <p className="text-xs font-medium text-[#556B2F] mt-0.5">{'\u00A3'}{(a.final_total || a.price || 0).toFixed(2)}</p>}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Invoices */}
          {invoices.length > 0 && (
            <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Invoices</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[300px] overflow-y-auto">
                  {invoices.map(inv => (
                    <div key={inv.id} className="flex items-center gap-3 px-5 py-3 border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6]">
                      <FileText size={14} className="text-[#556B2F]" />
                      <span className="font-mono text-xs text-[#556B2F]">{inv.invoice_number}</span>
                      <span className="text-sm text-[#1A2421] flex-1">{inv.service_name}</span>
                      <span className="text-sm text-[#5C6656]">{inv.date}</span>
                      <span className="text-sm font-semibold">{'\u00A3'}{(inv.final_total || 0).toFixed(2)}</span>
                      <Badge className={`text-[10px] ${inv.status === 'paid' ? 'bg-[#3E6641] text-white' : 'bg-[#E5E7E1] text-[#5C6656]'}`}>{inv.status}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Notes / Communication */}
        <div className="space-y-6">
          <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Notes & Communication</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={2} placeholder="Add a note..." className="border-[#E5E7E1] text-sm" data-testid="client-note-input" />
                <Button onClick={addNote} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white self-end px-3" data-testid="add-note-btn"><Send size={14} /></Button>
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {communications.map(c => (
                  <div key={c.id} className="p-3 rounded-lg bg-[#F9F8F6] border border-[#E5E7E1]">
                    <p className="text-sm text-[#1A2421]">{c.content}</p>
                    <p className="text-[10px] text-[#5C6656] mt-1">{c.created_by} · {new Date(c.created_at).toLocaleString()}</p>
                  </div>
                ))}
                {communications.length === 0 && <p className="text-center text-[#5C6656] text-xs py-4">No notes yet</p>}
              </div>
            </CardContent>
          </Card>

          {/* Client Notes from record */}
          {client.notes && (
            <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base">Client Notes</CardTitle></CardHeader>
              <CardContent><p className="text-sm text-[#5C6656]">{client.notes}</p></CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
